import { capitalize, isNil, last, get, compact } from "lodash"
import type { Country } from "resources/countries"
import { makeCustomFactorFormatter, factorFormatter } from "helpers/formatters/numbers"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import type { FactorRule, FactorRuleType } from "components/search-factor/types"
import { FACTOR_RULE_TYPES } from "components/search-factor/constants"
import { SimulationData, ContentSetWithEscapedId, Classification, ModelMetadata, FactorExposure, CompositionItem, ContentSetFactorData, FilterOption, SimulationCompositionItem, FactorRuleDisplay } from "./types"
import { FACTOR_RULE_INFO_LABELS, BETA_TYPES } from "./constants"

const COMPOSITION_CONFIGS = {
  COUNTRY: {
    type: FACTOR_RULE_TYPES.COUNTRY,
    category: "country",
    info: FACTOR_RULE_INFO_LABELS.COUNTRY,
  },
  CURRENCY: {
    type: FACTOR_RULE_TYPES.CURRENCY,
    category: "currency",
    info: FACTOR_RULE_INFO_LABELS.CURRENCY,
  },
}

function createRuleTransformer<T>(
  config: {
    getId: (item: T) => string
    getName: (item: T) => string
    getInfo: (item: T) => string
    getValue: (item: T) => number | undefined
    type: FactorRuleType
    getCategory?: (item: T) => string | undefined
    category?: string | undefined
    extraFields?: (item: T) => Partial<FactorRule>
  },
) {
  return (items: T[]): FactorRule[] => {
    return compact(items.map(item => {
      const category = config.category || config.getCategory?.(item)
      if (!category) return null

      return {
        id: config.getId(item),
        name: config.getName(item),
        info: config.getInfo(item),
        type: config.type,
        category,
        value: factorFormatter(config.getValue(item)),
        ...(config.extraFields?.(item) || {}),
      }
    }))
  }
}

export function transformFactorExposureToRules(
  exposure: SimulationData["exposure"],
  factorsMetadata: Record<string, NonNullable<ModelMetadata["factors"]>[number]>,
): FactorRule[] {
  if (!Array.isArray(exposure) || !exposure[0]?.factors) return []

  const transformer = createRuleTransformer<FactorExposure>({
    type: FACTOR_RULE_TYPES.FACTOR,
    getId: (factor) => factorsMetadata[factor.id]?.id || "",
    getName: (factor) => factorsMetadata[factor.id]?.label || "",
    getInfo: (factor) => capitalize(factorsMetadata[factor.id]?.category || ""),
    getValue: (factor) => factor.net,
    getCategory: (factor) => factorsMetadata[factor.id]?.category,
  })

  const validFactors = exposure[0].factors.filter(factor => factorsMetadata[factor.id])
  return transformer(validFactors)
}

export function transformContentSetFactorsToRules(
  simulationData: Partial<SimulationData>,
  contentSets: ContentSetWithEscapedId[] | null,
): FactorRule[] {
  if (!contentSets) return []

  return contentSets.flatMap(contentSet => {
    const isCurrency = contentSet.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY
    const escapedId = contentSet.escapedId || contentSet.id
    const factorArray: ContentSetFactorData[] | null = get(simulationData, [escapedId, 0, "factors"], null)

    if (!factorArray) return []

    const formatter = makeCustomFactorFormatter(isCurrency)

    return contentSet.factors
      .map(factor => {
        const factorData = factorArray.find(f => f.id === factor.id)
        if (!factorData) return null

        return {
          id: factor.id,
          name: factor.name,
          info: `Factor - ${contentSet.id}`,
          type: `${FACTOR_RULE_TYPES.FACTOR}-${contentSet.id}`,
          category: factor.category,
          value: !isNil(factorData.value) ? formatter(factorData.value) : undefined,
          isCurrency,
          contentSetId: contentSet.id,
          contentSetName: contentSet.name,
        } as FactorRule
      })
      .filter((rule): rule is FactorRule => rule !== null)
  })
}

export function transformBetasToRules(beta: SimulationData["beta"]): FactorRule[] {
  const betaData = beta?.[0]
  if (!betaData) return []

  const betaTypes = [BETA_TYPES.PREDICTED, BETA_TYPES.HISTORICAL]

  const validBetaEntries = betaTypes
    .filter(type => betaData[type] !== undefined)
    .map(type => ({ type, value: betaData[type] }))

  const transformer = createRuleTransformer<{ type: "predicted" | "historical", value: number | undefined }>({
    getId: (item) => item.type,
    getName: (item) => capitalize(item.type),
    getInfo: () => FACTOR_RULE_INFO_LABELS.BETA,
    getValue: (item) => item.value,
    type: FACTOR_RULE_TYPES.BETA,
    category: "beta",
  })

  return transformer(validBetaEntries)
}

function transformCompositionCategoryToRules(
  composition: CompositionItem[],
  compositionItems: Country[] | FilterOption[],
  ruleConfig: { type: FactorRuleType, category: string, info: string },
): FactorRule[] {
  const itemsLookup = new Map(compositionItems.map(item => [item.value, item.label]))

  const transformer = createRuleTransformer<CompositionItem>({
    getId: (item) => item.id,
    getName: (item) => itemsLookup.get(item.id) || "",
    getInfo: () => ruleConfig.info,
    getValue: (item) => item.economicExposure,
    type: ruleConfig.type,
    category: ruleConfig.category,
  })

  const validComposition = composition.filter(item => itemsLookup.has(item.id))
  return transformer(validComposition)
}

export function transformCompositionToRules(
  composition: SimulationData["composition"],
  metadata: { countries: Country[] | null, currencies: FilterOption[] | undefined, classifications: Classification[] | null },
): FactorRule[] {
  const compositionData = composition?.[0]
  if (!compositionData) return []

  const rules: FactorRule[] = []

  if (metadata.countries && compositionData.country) {
    rules.push(...transformCompositionCategoryToRules(compositionData.country, metadata.countries, COMPOSITION_CONFIGS.COUNTRY))
  }

  if (metadata.currencies && compositionData.currency) {
    rules.push(...transformCompositionCategoryToRules(compositionData.currency, metadata.currencies, COMPOSITION_CONFIGS.CURRENCY))
  }

  if (metadata.classifications) {
    rules.push(...transformClassificationsToRules(compositionData, metadata.classifications))
  }

  return rules
}

function getLatestTiers(classification: Classification) {
  return last(classification.versions)?.tiers || []
}

function createCompositionKey(classificationId: string, tierId: string): string {
  return `${classificationId}_${tierId}`
}

export function transformClassificationsToRules(
  composition: SimulationCompositionItem,
  classifications: Classification[] | null,
): FactorRule[] {
  if (!classifications || !composition) return []

  return classifications.flatMap(classification => {
    const tiers = getLatestTiers(classification)

    return tiers.flatMap(tier => {
      const compositionKey = createCompositionKey(classification.id, tier.id)
      const validComposition = composition[compositionKey] || []

      const transformer = createRuleTransformer<CompositionItem>({
        getId: (item) => item.id,
        getName: (item) => item.description,
        getInfo: () => `${classification.name} - ${tier.name || tier.id}`,
        getValue: (item) => item.economicExposure,
        type: FACTOR_RULE_TYPES.CLASSIFICATION,
        category: "classification",
        extraFields: () => ({
          groupById: classification.id,
          classificationTier: tier.id,
        }),
      })

      return transformer(validComposition)
    })
  })
}

export function createRuleKey(rule: Partial<FactorRule>): string {
  return `${rule.id || ""}-${rule.type || ""}-${rule.category || ""}`
}

export function filterExistingRules(rules: FactorRule[], existingRules: FactorRuleDisplay[]): FactorRule[] {
  const existingKeys = new Set(existingRules.map(createRuleKey))
  return rules.filter((rule) => !existingKeys.has(createRuleKey(rule)))
}

export function parseFieldValue(value: string | undefined): number | null {
  const parsedValue = value ? parseFloat(value) : null
  return isNaN(parsedValue as number) || parsedValue === null ? null : parsedValue
}
